import { zodResolver } from "@hookform/resolvers/zod";
import { clsx } from "clsx";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "sonner";

import { APIError } from "@/adapters/api";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { StageCombobox } from "@/features/stages/components/stage-combobox";
import type { StageTreeNode } from "@/features/stages/types.ts";
import { useSaveScheduleNote } from "../../hooks/use-save-schedule-note.ts";
import {
	NOTE_BODY_MAX,
	type NoteFormValues,
	noteSchema,
} from "../../schemas/note-schema.ts";
import type { Note, NoteSeverity } from "../../types.ts";
import { SEVERITIES, SEVERITY_LABELS } from "../../utils/severity.ts";
import { SeverityIcon } from "../severity-icon";

const VIOLATION_FIELDS: Record<string, keyof NoteFormValues> = {
	stage_id: "stageId",
	severity: "severity",
	body: "body",
};

const DESCRIPTION_MESSAGES: Record<string, string> = {
	required: "Campo obrigatório.",
	invalid: "Valor inválido.",
	not_found: "Etapa não encontrada nesta obra.",
	"max:1000": `A observação deve ter no máximo ${NOTE_BODY_MAX} caracteres.`,
};

function describe(description: string): string {
	return DESCRIPTION_MESSAGES[description] ?? description;
}

export interface NoteDraft {
	note?: Note;
	stageId?: string;
}

export function NoteForm({
	open,
	onOpenChange,
	constructionId,
	stages,
	draft,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	constructionId: string;
	stages: StageTreeNode[];
	draft: NoteDraft | null;
}) {
	const [formError, setFormError] = useState<string | null>(null);
	const save = useSaveScheduleNote(constructionId);
	const editing = draft?.note;

	const {
		control,
		handleSubmit,
		register,
		reset,
		setError,
		watch,
		formState: { errors },
	} = useForm<NoteFormValues>({
		resolver: zodResolver(noteSchema),
		defaultValues: { severity: "info", stageId: "", body: "" },
	});

	useEffect(() => {
		if (!open) return;
		reset({
			severity: draft?.note?.severity ?? "info",
			stageId: draft?.note?.stageId ?? draft?.stageId ?? "",
			body: draft?.note?.body ?? "",
		});
		setFormError(null);
	}, [open, draft, reset]);

	const bodyLength = watch("body").length;

	function applyAPIError(err: unknown, fallback: string) {
		if (err instanceof APIError) {
			let matched = false;
			for (const [field, description] of Object.entries(err.violations)) {
				const formField = VIOLATION_FIELDS[field];
				if (formField) {
					setError(formField, { message: describe(description) });
				} else {
					setFormError(describe(description));
				}
				matched = true;
			}
			if (!matched) setFormError(err.message || fallback);
			return;
		}
		setFormError(err instanceof Error ? err.message : fallback);
	}

	async function onSubmit(values: NoteFormValues) {
		setFormError(null);
		try {
			if (editing) {
				await save.mutateAsync({
					kind: "update",
					noteId: editing.id,
					input: {
						severity: values.severity,
						stageId: values.stageId || null,
						body: values.body,
					},
				});
				toast.success("Observação atualizada!");
			} else {
				await save.mutateAsync({
					kind: "create",
					input: {
						severity: values.severity,
						stageId: values.stageId || undefined,
						body: values.body,
					},
				});
				toast.success("Observação registrada!");
			}
			onOpenChange(false);
		} catch (err) {
			applyAPIError(err, "Não foi possível salvar a observação.");
		}
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>
						{editing ? "Editar observação" : "Nova observação"}
					</DialogTitle>
					<DialogDescription>
						Alertas e observações aparecem no Gantt e na impressão do
						cronograma.
					</DialogDescription>
				</DialogHeader>
				<form
					id="note-form"
					className="note-form"
					onSubmit={handleSubmit(onSubmit)}
					noValidate
				>
					<div className="note-form__fields">
						<div className="form-group">
							<Label htmlFor="note-severity">Severidade</Label>
							<Controller
								control={control}
								name="severity"
								render={({ field }) => (
									<Select
										value={field.value}
										onValueChange={(value) =>
											field.onChange(value as NoteSeverity)
										}
									>
										<SelectTrigger
											id="note-severity"
											aria-invalid={!!errors.severity}
											className="note-form__severity"
										>
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											{SEVERITIES.map((severity) => (
												<SelectItem key={severity} value={severity}>
													<SeverityIcon severity={severity} />
													{SEVERITY_LABELS[severity]}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								)}
							/>
							{errors.severity && (
								<p className="form-error">{errors.severity.message}</p>
							)}
						</div>
						<div className="form-group">
							<Label htmlFor="note-stage">Etapa</Label>
							<Controller
								control={control}
								name="stageId"
								render={({ field }) => (
									<StageCombobox
										id="note-stage"
										stages={stages}
										value={field.value}
										onChange={field.onChange}
										placeholder="Obra (geral)"
										noneLabel="Obra (geral)"
										modal
									/>
								)}
							/>
							{errors.stageId && (
								<p className="form-error">{errors.stageId.message}</p>
							)}
						</div>
					</div>

					<div className="form-group">
						<Label htmlFor="note-body">Observação</Label>
						<Textarea
							id="note-body"
							rows={4}
							maxLength={NOTE_BODY_MAX * 2}
							placeholder="Descreva o alerta, o risco ou a observação…"
							aria-invalid={!!errors.body}
							{...register("body")}
						/>
						<div className="note-form__foot">
							{errors.body ? (
								<p className="form-error">{errors.body.message}</p>
							) : (
								<span />
							)}
							<span
								className={clsx(
									"note-form__counter",
									bodyLength > NOTE_BODY_MAX && "is-over",
								)}
							>
								{bodyLength}/{NOTE_BODY_MAX}
							</span>
						</div>
					</div>

					{formError && (
						<p role="alert" className="form-error-alert">
							{formError}
						</p>
					)}
				</form>
				<DialogFooter>
					<Button
						type="button"
						variant="ghost"
						onClick={() => onOpenChange(false)}
						disabled={save.isPending}
					>
						Cancelar
					</Button>
					<Button type="submit" form="note-form" disabled={save.isPending}>
						{save.isPending && <Loader2 className="note-form__spinner" />}
						{editing ? "Salvar alterações" : "Registrar observação"}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
